// The detect_cluster behavior's pure core: the job, the prompt, the parse, the
// merge, the sweep plan and the behavior's own switches. One copy for both
// hosts of the runner (SPEC-custom-agent-template.md §4: "one runner, two hosts"):
//   browser — src/lib/llmTwinsService.ts and src/lib/clusterRunner.ts
//   server  — supabase/functions/ambient/lib/clusterServer.ts
//
// Given a batch of items, a cluster agent finds groups of two or more that are
// duplicates or variants of each other, for a person to accept (the items
// become an unresolved cluster to resolve) or dismiss.

import { buildInstructionStack, type AgentCard } from './instructionStackCore.ts';
import { fenceReferenceDocs } from './referenceDocFence.ts';
import { LANGUAGE_NAMES } from './estimateValueCore.ts';
import { jaccardSimilarity } from './itemTextCore.ts';

export const CLUSTER_BATCH_SIZE = 40;

// ── The job (canon doc 20) ────────────────────────────────────────────────────

/** The contract the model works to — the appendix of the instruction stack. */
export const CLUSTER_SYSTEM_PROMPT =
  'You are the Twins agent for a strategic prioritization system. ' +
  'Your job is to detect duplicate and variant item clusters. ' +
  'Respond with valid JSON only — no markdown, no extra text.';

export const CLUSTER_JOB = {
  capabilityId: 'twins',
  role: 'Find items that say the same thing, or close variants of it, for a person to review.',
  goal:
    'Keep the backlog honest: surface duplicates and variants before they are ranked twice, ' +
    'so the people who own them can resolve each group once.',
  outputContract:
    'Per group found: exactly one cluster proposal — two or more item ids, duplicate or variant, ' +
    'and a one- or two-sentence rationale. Items that are clearly distinct appear in no proposal.',
  skillStack: ['Semantic comparison', 'Duplicate versus variant judgment', 'Rationale compression'],
  reads: [
    'the titles and descriptions of the items in the batch',
    'reference documents an administrator added',
  ],
  proposes: ['cluster proposals: two or more items, duplicate or variant, with a rationale'],
  /** The platform bound on the model parameters; an administrator may lower them (§2 H). */
  modelDefaults: { temperature: 0.2, maxTokens: 1500 },
} as const;

export function clusterAgentCard(): AgentCard {
  return {
    capabilityId: CLUSTER_JOB.capabilityId,
    role: CLUSTER_JOB.role,
    goal: CLUSTER_JOB.goal,
    permissions: { read: [...CLUSTER_JOB.reads], propose: [...CLUSTER_JOB.proposes] },
    memory: 'turn',
    skillStack: [...CLUSTER_JOB.skillStack],
    outputContract: CLUSTER_JOB.outputContract,
  };
}

/**
 * The system prompt for a cluster run: the full instruction stack with the
 * workspace's text as the subordinate overlay — it specializes, it never
 * replaces the contract (§2 H). A language line is added when one is set.
 */
export function buildClusterSystemPrompt(params: {
  overlay?: string | null;
  language?: string | null;
}): { systemPrompt: string; l0Version: string; l1Version: string } {
  const overlay = params.overlay?.trim();
  const effectiveOverlay = overlay && overlay !== CLUSTER_SYSTEM_PROMPT ? overlay : null;
  const language = params.language && LANGUAGE_NAMES[params.language]
    ? `Write each rationale in ${LANGUAGE_NAMES[params.language]}. Keep ids, cluster types and JSON keys exactly as given.`
    : null;
  const { systemPrompt, meta } = buildInstructionStack({
    card: clusterAgentCard(),
    capabilityAppendix: [CLUSTER_SYSTEM_PROMPT, language].filter(Boolean).join('\n'),
    workspaceOverlay: effectiveOverlay,
  });
  return { systemPrompt, l0Version: meta.l0Version, l1Version: meta.l1Version };
}

// ── The behavior's own switches (agent_settings.behavior_config) ─────────────

export interface ClusterBehaviorConfig {
  /** Propose variant groups as well as duplicates. */
  includeVariants: boolean;
}

export const DEFAULT_CLUSTER_CONFIG: ClusterBehaviorConfig = { includeVariants: true };

export function resolveClusterConfigFrom(stored: Record<string, unknown> | null | undefined): ClusterBehaviorConfig {
  return { includeVariants: (stored ?? {}).includeVariants !== false };
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ClusterItemInput {
  id: string;
  title: string;
  description?: string | null;
}

export interface ClusterReferenceDoc {
  title: string;
  content: string;
  docType?: string;
}

export type ClusterType = 'duplicate' | 'variant';

export interface ClusterResult {
  item_ids: string[];
  cluster_type: ClusterType;
  rationale: string;
}

// ── Prompt builder ────────────────────────────────────────────────────────────

export function buildClusterPrompt(items: ClusterItemInput[], referenceDocs: ClusterReferenceDoc[] = []): string {
  const itemList = items
    .map(
      (item, i) =>
        `${i + 1}. ID: ${item.id}\n   Title: ${item.title}${
          item.description?.trim() ? `\n   Description: ${item.description.trim()}` : ''
        }`,
    )
    .join('\n\n');

  const fenced = fenceReferenceDocs(referenceDocs, 'what counts as the same work here');
  const refSection = fenced ? `\n\n${fenced}` : '';

  return `You are the Twins detection agent. Analyze the following items and identify groups of 2 or more that are duplicates or close variants of each other.

## Definitions
- duplicate: Items that express the same problem, need, or idea — even if worded differently. They would be resolved by the same work.
- variant: Items that are closely related variations on a theme — overlapping scope, similar goals, or the same root cause expressed from different angles. Distinct but would be better understood together.${refSection}

## Items to analyze
${itemList}

## Output Format
Return a JSON array of clusters. Include ONLY groups of 2 or more items. Items that are clearly distinct should not appear. If there are no clusters, return an empty array [].

Each cluster object:
{
  "item_ids": ["<uuid>", "<uuid>", ...],
  "cluster_type": "duplicate" | "variant",
  "rationale": "<1-2 sentences explaining why these items are grouped>"
}

Full response must be a valid JSON array, e.g.:
[
  { "item_ids": ["id1", "id2"], "cluster_type": "duplicate", "rationale": "Both express the same login friction problem." },
  { "item_ids": ["id3", "id4", "id5"], "cluster_type": "variant", "rationale": "All three address onboarding from different user perspectives." }
]`;
}

// ── Response parser ───────────────────────────────────────────────────────────

/**
 * Parse the model's reply. Ids not in the batch are dropped, groups of fewer
 * than two are dropped. Null when the reply is not a JSON array at all — a
 * failed call, not "no clusters" (never fabricate, §2 K).
 */
export function parseClusterResponse(content: string, validIds: Set<string>): ClusterResult[] | null {
  try {
    const arrayMatch = content.match(/\[[\s\S]*\]/);
    if (!arrayMatch) return null;

    const parsed = JSON.parse(arrayMatch[0]);
    if (!Array.isArray(parsed)) return null;

    return parsed.reduce<ClusterResult[]>((acc, item) => {
      const obj = item as Record<string, unknown>;
      if (!obj || !Array.isArray(obj.item_ids)) return acc;

      const item_ids = [...new Set((obj.item_ids as unknown[])
        .filter((id) => typeof id === 'string' && validIds.has(id as string)) as string[])];

      if (item_ids.length < 2) return acc;

      const cluster_type: ClusterType = obj.cluster_type === 'variant' ? 'variant' : 'duplicate';
      const rationale = typeof obj.rationale === 'string' ? obj.rationale.trim() : '';

      acc.push({ item_ids, cluster_type, rationale });
      return acc;
    }, []);
  } catch {
    return null;
  }
}

/** Groups that share an item become one group. */
export function mergeClusters(clusters: ClusterResult[]): ClusterResult[] {
  const merged: ClusterResult[] = [];

  for (const incoming of clusters) {
    const overlapping = merged.find((combined) =>
      incoming.item_ids.some((id) => combined.item_ids.includes(id)),
    );

    if (!overlapping) {
      merged.push({ ...incoming, item_ids: [...incoming.item_ids] });
      continue;
    }

    for (const id of incoming.item_ids) {
      if (!overlapping.item_ids.includes(id)) overlapping.item_ids.push(id);
    }
  }

  return merged;
}

/** Keep only the kinds the agent is set to propose. */
export function applyClusterConfig(clusters: ClusterResult[], config: ClusterBehaviorConfig): ClusterResult[] {
  return config.includeVariants ? clusters : clusters.filter((c) => c.cluster_type === 'duplicate');
}

/** The same members, in any order, are the same proposal (the idempotency key). */
export function clusterKey(itemIds: string[]): string {
  return [...itemIds].sort().join('|');
}

// ── Sweep: new items against the rest ─────────────────────────────────────────

/**
 * Plan the batches that check some items (new, or named by an event) against
 * the rest of the workspace: every batch holds all the target items and a
 * chunk of the others, the others most like the targets first, so a target
 * and its likely twin share the first batch. A proposal counts only when it
 * names at least one target (the caller filters with `touchesTargets`).
 */
export function planSweepBatches(
  targets: ClusterItemInput[],
  others: ClusterItemInput[],
  batchSize = CLUSTER_BATCH_SIZE,
  maxBatches = 5,
): ClusterItemInput[][] {
  if (!targets.length) return [];
  const targetIds = new Set(targets.map((t) => t.id));
  const pool = others.filter((o) => !targetIds.has(o.id));
  if (!pool.length) return targets.length >= 2 ? [targets.slice(0, batchSize)] : [];
  const textOf = (i: ClusterItemInput) => `${i.title} ${i.description ?? ''}`;
  const ranked = pool
    .map((o) => ({ o, score: Math.max(...targets.map((t) => jaccardSimilarity(textOf(t), textOf(o)))) }))
    .sort((a, b) => b.score - a.score)
    .map((r) => r.o);
  const head = targets.slice(0, Math.max(1, batchSize - 5));
  const chunk = Math.max(batchSize - head.length, 5);
  const batches: ClusterItemInput[][] = [];
  for (let offset = 0; offset < ranked.length && batches.length < maxBatches; offset += chunk) {
    batches.push([...head, ...ranked.slice(offset, offset + chunk)]);
  }
  return batches;
}

export function touchesTargets(cluster: ClusterResult, targetIds: Set<string>): boolean {
  return cluster.item_ids.some((id) => targetIds.has(id));
}
